import { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";
import toast from "react-hot-toast";
import Button from "@components/forms/Button/Button";
import TextField from "@components/forms/TextField/TextField";
import UserService from "@services/UserService";
import { connectUser } from "@slices/userSlice";
import "./LoginPage.scss";

function LoginPage() {
    const [isLogin, setLogin] = useState<boolean>(true);
    const [isFetching, setFetching] = useState<boolean>(false);

    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmation, setConfirmation] = useState<string>('');
    const [captchaValue, setCaptchaValue] = useState<string|null>(null);

    const service = new UserService;

    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();

    const handleChangeForm = () => {
        setLogin(!isLogin);
        setEmail('');
        setPassword('');
        setConfirmation('');
    };

    const handleLogin = () => {
        const login = service.call('GET', '/sanctum/csrf-cookie').then(() => service.login(email, password));
        setFetching(true);

        toast.promise(login, {
            loading: t('toast.connecting'),
            success: data => t('toast.welcome'),
            error: err => err.body && err.body.message || t('toast.error_fetching'),
        });

        login.then(data => {
            dispatch(connectUser(data.body));
            i18n.changeLanguage(data.body.language ?? 'en');
        }).catch(() => {
            setFetching(false);
        });
    };

    const handleSignup = () => {
        if(password !== confirmation) {
            toast.error(t('toast.error_password_confirmation'));
            return;
        }

        const signup = service.signup(email, password, captchaValue);

        toast.promise(signup, {
            loading: t('toast.creating'),
            success: data => t('toast.welcome'),
            error: err => err.body.errors ? Object.values(err.body.errors).join(', ') : t('toast.error_fetching'),
        });

        signup.then(data => {
            dispatch(connectUser(data.body));
        }).catch(() => {
        });
    };

    return (
        <div className="p-3">
            {isLogin ? (
                <div className="login-container">
                    <h1>{t('title.home')}</h1>

                    <form onSubmit={handleLogin}>
                        <div className="row gy-3">
                            <div className="col-12">
                                <TextField style="secondary" label={t('page.login.input.email.label')} value={email} placeholder={t('page.login.input.email.placeholder')} onChange={setEmail}/>
                            </div>

                            <div className="col-12">
                                <TextField style="secondary" label={t('page.login.input.password.label')} type="password" placeholder={t('page.login.input.password.placeholder')} value={password} onChange={setPassword}/>
                            </div>

                            <div className="col-12">
                                <Button filled onClick={handleLogin} disabled={isFetching || email === '' || password === ''}>{t('page.login.input.login')}</Button>
                                <div className="text-right mt-1">
                                    <Link to="/forgot-password">{t('page.login.forgot_password')}</Link>
                                </div>
                            </div>

                            <div className="col-12 text-center">
                                {t('text.or')}
                            </div>

                            <div className="col-12">
                                <Button filled className="secondary" onClick={handleChangeForm} disabled={isFetching}>{t('page.login.input.signup')}</Button>
                            </div>
                        </div>
                    </form>
                </div>
            ) : (
                <div className="login-container">
                    <h1>{t('title.signup')}</h1>

                    <div className="login-container row gy-3">
                        <div className="col-12">
                            <TextField style="secondary" label={t('page.signup.input.email.label')} value={email} placeholder={t('page.signup.input.email.placeholder')} onChange={setEmail}/>
                        </div>

                        <div className="col-12">
                            <TextField style="secondary" label={t('page.signup.input.password.label')} type="password" value={password} placeholder={t('page.signup.input.password.placeholder')} onChange={setPassword}/>
                        </div>

                        <div className="col-12">
                            <TextField style="secondary" label={t('page.signup.input.confirmation.label')} type="password" value={confirmation} placeholder={t('page.signup.input.confirmation.placeholder')} onChange={setConfirmation}/>
                        </div>

                        <div className="col-12">
                            <ReCAPTCHA
                                sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY ?? 'recaptcha_key'}
                                onChange={setCaptchaValue}
                            />
                        </div>

                        <div className="col-12">
                            <Trans i18nKey="page.signup.text_tos" components={{ lnk: <Link to="/tos" target="_blank"/> }} />
                        </div>

                        <div className="col-12">
                            <Button filled disabled={isFetching || email === '' || password === '' || confirmation === ''} onClick={handleSignup}>{t('page.signup.input.signup')}</Button>
                        </div>

                        <div className="col-12 text-center">
                            {t('text.or')}
                        </div>

                        <div className="col-12">
                            <Button filled className="secondary" onClick={handleChangeForm} disabled={isFetching}>{t('page.signup.input.login')}</Button>
                            <div className="text-right mt-1">
                                <Link to="/forgot-password">{t('page.login.forgot_password')}</Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LoginPage;
